import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { LoggerService } from "../services/logger.service";
import { UiToastService } from "../components/toast/ui-toast.service";

export type MissingDataRedirect =
  | { type: "navigate"; route: string[] }
  | { type: "back" };

export interface MissingDataCheckOptions {
  redirectOnMissingData: boolean;
  redirect: MissingDataRedirect;
  errorMessage: string;
  logMessage: string;
  componentTag: string;
}

/**
 * Centralizes the "if this piece of data is missing, redirect away + toast (initial load) or just log
 * it (background-triggered reload, never yank the user off a page they're already using)" guard used
 * by the BQ root components.
 */
@Injectable({ providedIn: "root" })
export class MissingDataService {
  constructor(
    private readonly toastService: UiToastService,
    private readonly logger: LoggerService,
    private readonly location: Location,
    private readonly router: Router
  ) { }

  /** Acts as a type guard so callers keep TypeScript's non-null narrowing on `value` after a false check. */
  isMissingData<T>(value: T | null | undefined, options: MissingDataCheckOptions): value is null | undefined {
    if (value) {
      return false;
    }

    if (options.redirectOnMissingData) {
      if (options.redirect.type === "navigate") {
        this.router.navigate(options.redirect.route);
      } else {
        this.location.back();
      }
      this.toastService.error(options.errorMessage);
    } else {
      this.logger.error(`[${options.componentTag}] ${options.logMessage}`);
    }

    return true;
  }
}

